import React from 'react'
import {Col, Row} from 'components/Display'
import {Problem, RouteMatch} from 'data/types'
import i18n from 'i18n'
import Portal from 'components/Portal'
import AddSolutionView from 'views/AddSolution'
import noSolutions from 'assets/no-solutions.svg'
import Button from 'components/Button'
import NextSteps from 'components/NextSteps'
import CommentSection from './CommentSection'
import CommentChatHeader from 'components/CommentChatHeader'
import CommentInput from 'views/CommentInput'
import services from 'services'
import {usePublished} from 'utils/published'
import getImageSrcFromBlob from 'utils/blob'

type FullProblemProps = {
  match: RouteMatch<{problemId: string}>
  isFullScreen: boolean
  setFullScreen: (v: boolean) => void
}

function FullScreenProblem(props: {problem: Problem; isOpen: boolean; close: () => void}) {
  return props.isOpen ? (
    <Portal>
      <Col className={`absolute top-0 left-0 w-full h-full z-50 full-screen-problem bg-black`} onClick={props.close}>
        <Col className="h-20" />
        <img className="flex-1 h-10 w-auto object-contain" src={props.problem.blob.url} alt={props.problem.title} />
        <Col className="h-20" />
      </Col>
    </Portal>
  ) : null
}

export default function FullProblem(props: FullProblemProps) {
  const service = services.getSingleProblemService(props.match.params.problemId)
  const problem = usePublished(service.problem)

  if (problem == null) {
    return null
  }

  return (
    <React.Fragment>
      <FullScreenProblem problem={problem} isOpen={props.isFullScreen} close={() => props.setFullScreen(false)} />
      <Col className="flex-1 overflow-y-scroll">
        <Col className="pt-2">
          <div className="px-8">
            {/* Title */}
            <Col className="pt-4">
              <div className="text-itx-gray-dark font-bold text-xs 2xl:text-xl pb-2">{i18n.t('problems.problem')}</div>
              <div className="text-gray-itx-content-text font-bold text-xl 2xl:text-3xl">{problem.title}</div>
            </Col>

            {/* Description */}
            <div className="text-gray-itx-content-text font-normal mt-8 pb-8">{problem.description}</div>

            {/* Image */}
            <Row className="flex h-72 mt-5 sm:mt-8 md:h-56 h-70 2xl:h-72 bg-gray-itx-border border rounded-md mb-20">
              <img
                className="w-auto flex-1 h-full object-contain hover:shadow transition duration-200 cursor-pointer"
                onClick={() => props.setFullScreen(true)}
                src={getImageSrcFromBlob(problem.blob)}
                alt={problem.title}
              />
            </Row>
            {problem.solutionsCount > 0 && (
              <Row className="mb-6 justify-between bg-itx-primary-4  rounded-lg px-4 py-2 items-center">
                <div className="text-white font-bold">You have {i18n.t('solutions.counting', {count: problem.solutionsCount})}</div>
                <Button className="bg-white text-itx-primary-3" content="View solutions" onClick={() => services.router.selectSolutions(problem)} />
              </Row>
            )}
          </div>

          {problem.solutionsCount === 0 && !problem.isValidated && (
            <div className="px-8">
              <NextSteps solutionsCount={problem.solutionsCount} />
            </div>
          )}

          {problem.solutionsCount === 0 && problem.isValidated && (
            <div className="px-8">
              <Col>
                <Row className="justify-between">
                  <Col>
                    <div className="text-itx-primary-base text-xs font-bold">{i18n.t('solutions.nextStep')}</div>
                    <div className="text-itx-primary-base text-xl font-bold ">{i18n.t('solutions.exploreSolutions')}</div>
                  </Col>
                  <Col className="justify-end">
                    <AddSolutionView problem={problem} />
                  </Col>
                </Row>

                <Row className="mt-2 sm:mt-5">
                  <img className={`w-full`} alt={'noSolutions'} src={noSolutions} />
                </Row>
              </Col>
            </div>
          )}
          <div className="bg-blue-itx-light mt-8 border-t">
            <CommentSection
              threadId={problem.threadId}
              header={<CommentChatHeader user={problem.creator} i18nPlaceholder="problems.createdThisProblem" createdAt={problem.createdAt} />}
            />
          </div>
        </Col>
      </Col>
      <div>
        <CommentInput threadId={problem.threadId} />
      </div>
    </React.Fragment>
  )
}
